import { useId } from 'react'
import CaseBadge from './CaseBadge'
import Icon from './Icon'
import styles from './NewSkillCard.module.css'

/**
 * NewSkillCard — a reviewable skill candidate with evidence cases and
 * merge / approve / reject actions, shown in the Skills Proposals panel.
 *
 * Proposed skills have no classification yet (the skills_candidates table carries
 * no skill_type), so no type pill is rendered — only the skill name.
 *
 * Props:
 *   skillId    {number|string} — skills_candidates.skill_id (the candidate id); forwarded
 *                         to each CaseBadge so its evidence modal fetches from Postgres
 *                         (candidates have no Neo4j presence until approved/merged)
 *   skillName  {string} — candidate name; forwarded to CaseBadge as the
 *                         primary skill to auto-select in the evidence modal
 *   description {string} — optional evidence-based skill description; when present,
 *                         a second info icon shows it on hover/focus. Hidden entirely
 *                         when absent (no placeholder text).
 *   tickets    {Object} — map of { ticketNumber: reason }. Only the first six
 *                         tickets are shown on the card; each opens its evidence modal.
 *                         The skill name is suffixed with "(<count>)" when non-empty.
 *                         Any additional tickets beyond the first six sit behind a
 *                         small "+N" pill — hover/focus reveals them as their own
 *                         clickable evidence badges.
 *   onApprove  {function} — called when Approve is clicked
 *   onReject   {function} — called when Reject is clicked
 *   onMerge    {function} — merge into an existing active skill
 *   status          {string} — normalized | proposed | rejected
 *   rejectedReason  {string} — optional reviewer-entered rejection reason
 *   reviewedBy      {string} — reviewer display name
 *   reviewedAt      {string} — rejection timestamp
 */
export default function NewSkillCard({
  skillId,
  skillName,
  description,
  status = 'proposed',
  tickets = {},
  rejectedReason,
  reviewedBy,
  reviewedAt,
  onApprove,
  onReject,
  onMerge,
}) {
  // The card surfaces at most six evidence cases; the rest sit behind a "+N more" pill.
  const allCases = Object.keys(tickets)
  const cases = allCases.slice(0, 6)
  const overflowCases = allCases.slice(6)
  const rejectionTooltipId = useId()
  const descriptionTooltipId = useId()
  const overflowTooltipId = useId()
  const statusLabel = status === 'normalized' ? 'Candidate' : `${status[0].toUpperCase()}${status.slice(1)}`
  const reviewedDate = formatReviewedDate(reviewedAt)
  const isRejected = status === 'rejected'
  const hasDescription = Boolean(description)

  return (
    <div className={styles.card}>

      {/* Header — skill name + hover/focus details tooltip(s) */}
      <div className={`${styles.header} ${(isRejected || hasDescription) ? styles.headerWithInfo : ''}`}>
        <h3 className={styles.skillName}>
          {skillName}{allCases.length > 0 ? ` (${allCases.length})` : ''}
        </h3>
        {(isRejected || hasDescription) && (
          <div className={styles.infoCluster}>
            {isRejected && (
              <div className={styles.infoWrap}>
                <button
                  type="button"
                  className={styles.infoBtn}
                  aria-label={`Rejection details for ${skillName}`}
                  aria-describedby={rejectionTooltipId}
                >
                  <Icon name="info" />
                </button>
                <div id={rejectionTooltipId} className={styles.tooltip} role="tooltip">
                  <span className={styles.tooltipStatus}>{statusLabel}</span>
                  <span className={styles.tooltipReason}>
                    {rejectedReason || 'No rejection reason provided.'}
                  </span>
                  <span className={styles.tooltipReview}>
                    Rejected by {reviewedBy || 'Unknown reviewer'}{reviewedDate ? ` on ${reviewedDate}` : ''}
                  </span>
                </div>
              </div>
            )}
            {hasDescription && (
              <div className={styles.infoWrap}>
                <button
                  type="button"
                  className={styles.infoBtn}
                  aria-label={`Description for ${skillName}`}
                  aria-describedby={descriptionTooltipId}
                >
                  <Icon name="info" />
                </button>
                <div id={descriptionTooltipId} className={styles.tooltip} role="tooltip">
                  <span className={styles.tooltipReason}>{description}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Evidence cases */}
      <div className={styles.evidenceSection}>
        <span className={styles.evidenceLabel}>Evidence Cases:</span>
        <div className={styles.badgeGrid}>
          {cases.map(caseId => (
            <CaseBadge key={caseId} caseId={caseId} primarySkill={skillName} candidateSkillId={skillId} />
          ))}
          {overflowCases.length > 0 && (
            <div className={styles.moreWrap}>
              <button
                type="button"
                className={styles.moreBtn}
                aria-label={`${overflowCases.length} more evidence case${overflowCases.length === 1 ? '' : 's'} for ${skillName}`}
                aria-describedby={overflowTooltipId}
              >
                +{overflowCases.length}
              </button>
              <div id={overflowTooltipId} className={styles.moreTooltip} role="tooltip">
                {overflowCases.map(caseId => (
                  <CaseBadge key={caseId} caseId={caseId} primarySkill={skillName} candidateSkillId={skillId} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Every displayed candidate state is reviewable. */}
      <div className={styles.actions}>
        <button
          type="button"
          className={`${styles.actionBtn} ${styles.mergeBtn}`}
          onClick={onMerge}
          title="Merge skill"
          aria-label={`Merge ${skillName} with an existing skill`}
        >
          <Icon name="shuffle" />
          Merge
        </button>
        <button
          type="button"
          className={`${styles.actionBtn} ${styles.approveBtn}`}
          onClick={onApprove}
          title="Approve skill"
          aria-label={`Approve ${skillName}`}
        >
          <Icon name="check" />
          Approve
        </button>
        <button
          type="button"
          className={`${styles.actionBtn} ${styles.rejectBtn}`}
          onClick={onReject}
          title="Reject skill"
          aria-label={`Reject ${skillName}`}
        >
          <Icon name="close" />
          Reject
        </button>
      </div>

    </div>
  )
}

function formatReviewedDate(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  }).format(date)
}
